import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import { supabase } from "@/lib/supabase";

/**
 * Organization branding — `display_name`, `logo_url`, `brand_primary_hex`,
 * `brand_accent_hex` — read once per signed-in session and exposed to
 * anything that needs to render a header chip or accent line.
 *
 * Scope is intentionally restrained: we only inject brand colours into
 * header surfaces + a small set of accents. The rest of the Tailwind
 * palette stays stable so existing component styling isn't
 * accidentally repainted.
 *
 * Parents + teachers CAN read `public.organizations` via the existing
 * `organizations_member_select` RLS policy (`id =
 * current_organization_id()`), so this works for every role. On failure
 * we fall back to the platform defaults so the UI never renders an
 * empty header.
 */

const DEFAULTS = {
  displayName: "Daycare",
  logoUrl: null,
  primary: "#C6851E",
  accent: "#EFBA57",
  loaded: false,
};

const BrandingContext = createContext(DEFAULTS);

export function BrandingProvider({ children }) {
  const { profile } = useAuth();
  const orgId = profile?.organization_id || null;
  const [branding, setBranding] = useState(DEFAULTS);

  useEffect(() => {
    if (!orgId) {
      setBranding(DEFAULTS);
      return;
    }
    let cancelled = false;

    // Seed from session cache so the header doesn't flicker between
    // the defaults and the real branding on every page load.
    try {
      const cached = sessionStorage.getItem(`branding:${orgId}`);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.primary) setBranding({ ...parsed, loaded: true });
      }
    } catch {
      // sessionStorage can be unavailable in some browsers; ignore.
    }

    (async () => {
      try {
        const { data, error } = await supabase
          .from("organizations")
          .select("display_name, name, logo_url, brand_primary_hex, brand_accent_hex")
          .eq("id", orgId)
          .maybeSingle();
        if (error || !data || cancelled) return;
        const next = {
          displayName: data.display_name || data.name || DEFAULTS.displayName,
          logoUrl: data.logo_url || null,
          primary: data.brand_primary_hex || DEFAULTS.primary,
          accent: data.brand_accent_hex || DEFAULTS.accent,
          loaded: true,
        };
        setBranding(next);
        try {
          sessionStorage.setItem(`branding:${orgId}`, JSON.stringify(next));
        } catch {
          // ignore
        }
      } catch {
        // Keep whatever we already have (cache or defaults).
      }
    })();

    return () => { cancelled = true; };
  }, [orgId]);

  // Expose CSS variables for consumers that want to style accents
  // without importing the hex directly.
  const cssVars = useMemo(
    () => ({
      "--brand-primary": branding.primary,
      "--brand-accent": branding.accent,
    }),
    [branding.primary, branding.accent],
  );

  const value = useMemo(() => ({ ...branding, cssVars }), [branding, cssVars]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  return useContext(BrandingContext);
}

/**
 * Reusable logo chip used by the desktop sidebar + the mobile header.
 * Falls back to the first initial of the display name with a primary
 * tint when no logo_url is configured.
 */
export function BrandLogoChip({ size = 40, testId }) {
  const { logoUrl, displayName, primary } = useBranding();
  const initial = (displayName || "D").trim().slice(0, 1).toUpperCase();
  return (
    <div
      data-testid={testId || "brand-logo-chip"}
      className="flex flex-shrink-0 items-center justify-center overflow-hidden rounded-xl"
      style={{
        width: size,
        height: size,
        backgroundColor: logoUrl ? "#ffffff" : `${primary}1A`, // 10% tint when no logo
      }}
    >
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          aria-label={displayName}
          className="h-full w-full object-cover"
        />
      ) : (
        <span
          className="font-heading font-semibold"
          style={{ color: primary, fontSize: Math.round(size * 0.45) }}
        >
          {initial}
        </span>
      )}
    </div>
  );
}
